import { DraggableAttributes } from "@dnd-kit/core";
import { ChatBotSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { TextInput } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { FC } from "react";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { AppearanceSettingsTabShell } from "../AppearanceSettingsTabShell";
import { Separator } from "../../../../components/Separator";
import { TabHeader } from "../../../../components/TabHeader";
import {
  DraggableList,
  DragHandle,
  SortableItem,
} from "../../../../components/DraggableList";

export const SuggestionsTabContent = () => {
  const { t } = useTranslations();

  return (
    <AppearanceSettingsTabShell tabId="suggestions" previewMode="suggestions">
      <TabHeader
        title={t({
          id: "client-support-page.quick-a",
          defaultMessage: "Quick Actions",
        })}
        subtitle={t({
          id: "client-support-page.quick-actions-tab-subtitle",
          defaultMessage:
            "Add quick buttons for common requests to help visitors start conversation faster",
        })}
      />
      <Separator className="my-8" />
      <SuggestionsTabContentInner />
    </AppearanceSettingsTabShell>
  );
};

const SuggestionsTabContentInner: FC = () => {
  const { watch, control } = useFormContext<ChatBotSettingsFormValues>();

  const { fields, move } = useFieldArray({
    control,
    name: "suggestions",
  });

  const suggestions = watch("suggestions") || [];
  const filledCount = suggestions.filter((s) => s.value.trim() !== "").length;
  const isDragDisabled = filledCount < 2;

  return (
    <div className="flex flex-col gap-6">
      <DraggableList
        items={fields}
        getId={(item) => item.id}
        onReorder={move}
        isDragDisabled={isDragDisabled}
        renderItem={(item, index, disabled) => (
          <SortableItem key={item.id} id={item.id} disabled={disabled}>
            {({ dragHandleProps }) => (
              <SuggestionInput
                index={index}
                disabled={disabled}
                dragHandleProps={dragHandleProps}
              />
            )}
          </SortableItem>
        )}
      />
    </div>
  );
};

const SuggestionInput: FC<{
  index: number;
  disabled: boolean;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap | undefined;
  };
}> = ({ index, disabled, dragHandleProps }) => {
  const { t } = useTranslations();
  const { control } = useFormContext<ChatBotSettingsFormValues>();

  return (
    <div className="flex items-center gap-2">
      <Controller
        control={control}
        name={`suggestions.${index}.value`}
        render={({ field: { onChange, ...props } }) => (
          <TextInput
            labelPosition="border-overlap"
            label={t(
              {
                id: "client-support-page.action-number",
                defaultMessage: "Action {number}",
              },
              { number: index + 1 }
            )}
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            classNames={{
              wrapperClassName: "w-full",
            }}
            onChange={(e) => onChange(e.target.value.trimStart())}
            {...props}
          />
        )}
      />
      <DragHandle disabled={disabled} dragHandleProps={dragHandleProps} />
    </div>
  );
};
